import React, { useState } from 'react';
import { apiService, parseLimitError, LimitInfo } from '../services/api';
import { StorageProvider } from '../types';
import { getCurrentUserType } from '../amplify-config';
import UpgradePromptModal from './UpgradePromptModal/UpgradePromptModal';

const OVH_REGIONS = [
  { code: 'cph', label: 'Northern Europe (Copenhagen)' },
  { code: 'mil', label: 'Southern Europe (Milan)' },
  { code: 'de',  label: 'Central Europe (Germany)' },
  { code: 'par', label: 'Western Europe (Paris)' },
  { code: 'uk',  label: 'United Kingdom' },
  { code: 'bhs', label: 'United States (Beauharnois)' },
  { code: 'tor', label: 'Canada (Toronto)' },
  { code: 'sgp', label: 'Asia Pacific (Singapore)' },
  { code: 'syd', label: 'Australia (Sydney)' },
  { code: 'rba', label: 'Africa (Rabat)' },
];

interface CreateBucketFormProps {
  onBucketCreated: () => void;
  isAdmin?: boolean;
}

const CreateBucketForm: React.FC<CreateBucketFormProps> = ({ onBucketCreated, isAdmin }) => {
  const userType = getCurrentUserType();
  const [bucketName, setBucketName] = useState('');
  const [provider, setProvider] = useState<StorageProvider>(userType === 'personal' ? 'OVH' : 'S3');
  const [regionOverride, setRegionOverride] = useState('par');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<LimitInfo | null>(null);

  const validateBucketName = (name: string): string | null => {
    if (!name) return 'Data crate name is required';
    if (name.length < 3 || name.length > 30) {
      return 'Data crate name must be between 3 and 30 characters';
    }
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(name)) {
      return 'Data crate name must be lowercase letters, numbers, and hyphens only';
    }
    if (/--/.test(name)) {
      return 'Data crate name cannot contain consecutive hyphens';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateBucketName(bucketName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await apiService.createBucket({
        bucketName,
        provider,
        ...(isAdmin && provider === 'OVH' ? { regionOverride } : {})
      });

      // Show success popup
      alert(`✅ Data crate "${bucketName}" created successfully!\n\nPlease refresh the "My Data Crates" section to see your new crate.`);
      
      setBucketName('');
      setProvider(userType === 'personal' ? 'OVH' : 'S3');
      onBucketCreated();
    } catch (err: any) {
      console.error('Create bucket error:', err);
      
      // Handle 403 errors with limit information
      if (err.statusCode === 403 && err.errorData?.error) {
        const limitInfo = parseLimitError(err.errorData.error);
        if (limitInfo && limitInfo.limitType === 'crates') {
          setLimitInfo(limitInfo);
          setShowUpgradeModal(true);
          setError(''); // Clear error since we're showing the modal
        } else {
          // Handle other 403 errors (e.g., personal users trying to create S3 buckets)
          if (err.message?.includes('Personal users cannot create AWS S3 buckets')) {
            setError('Personal accounts cannot create AWS S3 buckets. Please select OVHcloud.');
          } else {
            setError(err.message || 'You do not have permission to create this data crate.');
          }
        }
      } else {
        setError(err.message || 'Failed to create data crate. Please try again.');
      }
      
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {limitInfo && (
        <UpgradePromptModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setLimitInfo(null);
          }}
          limitType={limitInfo.limitType}
          currentUsage={limitInfo.currentUsage}
          limit={limitInfo.limit}
          currentTier={limitInfo.currentTier}
        />
      )}
      
      <div className="bucket-list-container">
      <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
        Create Your Data Crate
      </h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          {/* Personal users: DC4S (OVH) only */}
          {userType === 'personal' && (
            <>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="provider"
                    value="OVH"
                    checked={provider === 'OVH'}
                    onChange={(e) => setProvider(e.target.value as StorageProvider)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>DC4S</span>
                </label>
              </div>
            </>
          )}

          {/* Business users: S3 and OVH options */}
          {userType === 'business' && (
            <>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="provider"
                    value="S3"
                    checked={provider === 'S3'}
                    onChange={(e) => setProvider(e.target.value as StorageProvider)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>AWS S3</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="provider"
                    value="OVH"
                    checked={provider === 'OVH'}
                    onChange={(e) => setProvider(e.target.value as StorageProvider)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>OVHcloud</span>
                </label>
              </div>
            </>
          )}

          {/* Provider description cards */}
          {provider === 'S3' && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem'
            }}>
              <strong>Amazon S3:</strong> Cloud object storage with high availability and security
            </div>
          )}
          {provider === 'OVH' && userType === 'personal' && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#fff3cd',
              borderLeft: '4px solid #ffc107',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem'
            }}>
              <strong>DC4S:</strong> Secure enterprise-grade S3-compatible cloud storage.
            </div>
          )}
          {provider === 'OVH' && userType === 'business' && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#e8f5e9',
              borderLeft: '4px solid #43a047',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem'
            }}>
              <strong>OVHcloud:</strong> S3-compatible object storage hosted in OVH datacentres
            </div>
          )}
        </div>

        {isAdmin && provider === 'OVH' && (
          <div className="form-group">
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
              Region
            </label>
            <select
              className="form-input"
              value={regionOverride}
              onChange={e => setRegionOverride(e.target.value)}
              style={{ cursor: 'pointer' }}
            >
              {OVH_REGIONS.map(r => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <input
            type="text"
            className="form-input"
            placeholder="Enter data crate name (lowercase, 3-30 chars)"
            value={bucketName}
            onChange={(e) => setBucketName(e.target.value.toLowerCase())}
            maxLength={30}
          />
        </div>

        {error && <div className="form-error">{error}</div>}

        <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', position: 'relative' }}>
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <span style={{
                display: 'inline-block',
                width: '16px',
                height: '16px',
                border: '3px solid rgba(255, 255, 255, 0.3)',
                borderTop: '3px solid white',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}></span>
              Creating...
            </span>
          ) : (
            'Create Data Crate'
          )}
        </button>
      </form>
    </div>
    </>
  );
};

export default CreateBucketForm;
